import React, { useState } from "react";
import { Send } from "lucide-react";

// Chat-style thread for a single segnalazione. Renders the original client
// message plus all replies as bubbles. `mineSender` decides which side is
// right-aligned ("client" in the client/preview view, "admin" in the panel).
// Pass onReply=null for a read-only thread.
const THEMES = {
  dark: { mineBg: "#E05A2C", mineInk: "#fff", otherBg: "#1C1815", otherInk: "#FAF7F2", border: "#2A241F", muted: "#857C72", inputBg: "#12100E", focus: "#E05A2C" },
  light: { mineBg: "#D9501F", mineInk: "#fff", otherBg: "#F5F3EF", otherInk: "#141210", border: "#E3DFD9", muted: "#5B554F", inputBg: "#FFFFFF", focus: "#D9501F" },
};

const STATUS = {
  unread: { label: "Da leggere", color: "#D4A359" },
  read: { label: "Letta", color: "#8a8078" },
  replied: { label: "Risposta", color: "#4F9D69" },
};

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function MessageThread({ message, mineSender = "client", onReply = null, theme = "dark" }) {
  const t = THEMES[theme] || THEMES.dark;
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const items = [
    { sender: "client", text: message.text, created_at: message.submitted_at, author: message.client_email },
    ...(message.replies || []),
  ];

  const st = STATUS[message.status] || STATUS.unread;

  const submit = async (e) => {
    e.preventDefault();
    if (!onReply || !text.trim() || busy) return;
    setBusy(true);
    try {
      await onReply(text.trim());
      setText("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: t.border }}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: t.muted }}>
          {message.structure_name ? `${message.structure_name} · ` : ""}{fmtDate(message.submitted_at)}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: st.color }}>{st.label}</span>
      </div>

      <div className="space-y-2.5">
        {items.map((it, i) => {
          const mine = it.sender === mineSender;
          return (
            <div key={it.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className="max-w-[82%] rounded-2xl px-4 py-2.5"
                style={{
                  background: mine ? t.mineBg : t.otherBg,
                  color: mine ? t.mineInk : t.otherInk,
                  border: mine ? "none" : `1px solid ${t.border}`,
                  borderBottomRightRadius: mine ? 6 : 16,
                  borderBottomLeftRadius: mine ? 16 : 6,
                }}>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{it.text}</div>
                <div className="mt-1 text-[10px] font-mono opacity-70">
                  {it.sender === "admin" ? "Specialista" : "Cliente"} · {fmtDate(it.created_at)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {onReply ? (
        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Scrivi una risposta…"
            className="flex-1 rounded-full border px-4 py-2 text-sm outline-none transition-colors"
            style={{ background: t.inputBg, borderColor: t.border, color: t.otherInk }}
            onFocus={(e) => (e.target.style.borderColor = t.focus)}
            onBlur={(e) => (e.target.style.borderColor = t.border)}
          />
          <button type="submit" disabled={busy || !text.trim()}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            style={{ background: t.mineBg }}>
            <Send className="h-4 w-4" /> {busy ? "…" : "Invia"}
          </button>
        </form>
      ) : null}
    </div>
  );
}
